import { useState, useEffect, useCallback } from "react";
import { Task } from "../../models/task.model";
import { getTasks, removeTask, updateTaskStatus } from "../../api/taskManager";
import { AddTaskModal } from "../../components/task/modals/AddTaskModal";
import { TaskDetailsModal } from "../../components/task/modals/TaskDetailsModal";


export default function TaskPage() {
  const [listElements, setListElements] = useState<Task[]>([]);
  const [spinner, setSpinner] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<'ALL' | 'TODO' | 'IN_PROGRESS' | 'DONE'>('ALL');
  
  // Modalok állapotai
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false);
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  // Adatok betöltése
  const getList = useCallback(async () => {
    setSpinner(true);
    try {
      const filterStatus = selectedStatus === 'ALL' ? undefined : selectedStatus;
      const res = await getTasks(filterStatus);
      setListElements(res);
    } catch (error) {
      console.error("Nem sikerült betölteni a feladatokat", error);
    } finally {
      setSpinner(false);
    }
  }, [selectedStatus]);

  useEffect(() => {
    getList();
  }, [getList]);

  // Törlés kezelése
  const handleDelete = useCallback(async (id: string) => {
    if (!window.confirm("Biztosan törölni szeretnéd ezt a feladatot?")) return;

    try {
      await removeTask(id);
      setListElements(prev => prev.filter(task => task.id !== id));
    } catch (error) {
      alert("Hiba történt a feladat törlésekor.");
    }
  }, []);

  // Részletek megnyitása gomb kezelője
  const handleViewDetails = useCallback((task: Task) => {
    setActiveTask(task);
    setIsDetailsModalOpen(true);
  }, []);

  const getStatusBadgeClass = useCallback((state: 'TODO' | 'IN_PROGRESS' | 'DONE') => {
    switch (state) {
      case 'TODO': return "bg-blue-100 text-blue-800";
      case 'IN_PROGRESS': return "bg-amber-100 text-amber-800";
      case 'DONE': return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  }, []);

  return (
    <div className="m-5 p-5 bg-gray-100 rounded-lg shadow-lg">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
            <h2 className="text-xl font-semibold text-gray-800">Feladatok Kezelése (Admin)</h2>
            <p className="text-xs text-gray-400">Rendszer szintű feladatok monitorozása</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-2 bg-white p-1 rounded-md shadow-sm border border-gray-200">
            {(['ALL', 'TODO', 'IN_PROGRESS', 'DONE'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setSelectedStatus(status)}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${
                  selectedStatus === status
                    ? "bg-gray-800 text-white shadow"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {status === 'ALL' ? 'Összes' : status}
              </button>
            ))}
          </div>

          <button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase tracking-wider px-4 py-2.5 rounded-md shadow-md transition-all active:scale-95"
          >
            + Új Feladat
          </button>
        </div>
      </div>

      {spinner ? (
        <p className="text-center text-2xl animate-pulse py-10 text-gray-600">
          Kérlek várj még az adatok betöltődnek...
        </p>
      ) : listElements.length === 0 ? (
        <p className="text-center py-10 text-gray-500 font-medium">Nincs megjeleníthető feladat ebben a kategóriában.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-y-1">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Azonosító</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Létrehozva</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Cím</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Létrehozó</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Felelős</th>
                <th className="px-4 py-2 text-center text-sm font-medium text-gray-600">Állapot</th>
                <th className="px-4 py-2 text-center text-sm font-medium text-gray-600">Műveletek</th>
              </tr>
            </thead>
            <tbody>
              {listElements.map((item, index) => (
                <tr key={item.id} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className="px-4 py-2 text-sm font-semibold text-gray-500" title={item.id}>
                    {item.id.substring(0, 8)}...
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-800">
                    {new Date(item.date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-sm font-semibold text-gray-800">
                    {item.title}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-800 font-medium">
                    {item.creator?.name || `ID: ${item.creatorId}`}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-800 font-medium">
                    {item.assignee?.name || `ID: ${item.assigneeId}`}
                  </td>
                  <td className="px-4 py-2 text-sm text-center">
                <select
                    value={item.taskState}
                    onChange={async (e) => {
                    const nextState = e.target.value as 'TODO' | 'IN_PROGRESS' | 'DONE';
                    try {
                        // API hívás a háttérben
                        await updateTaskStatus(item.id, nextState);
                        
                        // Lokális state frissítése, hogy a felület azonnal reagáljon
                        setListElements(prev => 
                        prev.map(task => task.id === item.id ? { ...task, taskState: nextState } : task)
                        );
                    } catch (error) {
                        alert("Nem sikerült módosítani a feladat állapotát.");
                    }
                    }}
                    className={`px-2 py-1 rounded text-xs font-bold border outline-none cursor-pointer shadow-sm transition-all ${
                    item.taskState === 'TODO' ? "bg-blue-100 text-blue-800 border-blue-200" :
                    item.taskState === 'IN_PROGRESS' ? "bg-amber-100 text-amber-800 border-amber-200" :
                    "bg-green-100 text-green-800 border-green-200"
                    }`}
                >
                    <option value="TODO" className="bg-white text-blue-800 font-bold">TODO</option>
                    <option value="IN_PROGRESS" className="bg-white text-amber-800 font-bold">IN PROGRESS</option>
                    <option value="DONE" className="bg-white text-green-800 font-bold">DONE</option>
                </select>
                </td>
                  <td className="px-4 py-2 text-sm text-center space-x-2">
                    {/* Megtekintés Gomb */}
                    <button
                      onClick={() => handleViewDetails(item)}
                      className="px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded border border-slate-200 transition-colors"
                    >
                      Megtekintés
                    </button>
                    {/* Törlés Gomb */}
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 rounded border border-red-200 transition-colors"
                    >
                      Törlés
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Új feladat Modal */}
      <AddTaskModal 
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
            setIsAddModalOpen(false);
            getList();
        }}
      />

      {/* Részletek megjelenítése Modal */}
      <TaskDetailsModal 
        isOpen={isDetailsModalOpen}
        task={activeTask}
        onClose={() => {
            setIsDetailsModalOpen(false);
            setActiveTask(null);
        }}
      />
    </div>
  );
}